import { useState, useEffect } from "react";
import { contentfulClient } from "../services/api.ts";
import { useGlobalContext } from "../utils/GlobalContext.ts";
import { ContactUsPage } from "../graphql/graphql.ts";

const GetContactUsPage = `
  query GetContactUsPage($locale: String) {
    contactUsPageCollection(locale: $locale) {
      items {
        pageTitle
        backButtonLabel
        description
        submitButtonLabel
        submittedRequestTitle
        submittedRequestDescription
        googleFormId
      }
    }
  }
`;

interface ContactUsPageResponse {
  contactUsPageCollection?: {
    items: ContactUsPage[];
  } | null;
}

interface ContactUsPageContent {
  pageData: ContactUsPage | null;
  loading: boolean;
  notFound: boolean;
  currentLocale: string | null;
}

export function useContactUsPageContent(): ContactUsPageContent {
  const { localeConfig } = useGlobalContext();
  const [pageData, setPageData] = useState<ContactUsPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!localeConfig.currentLocale) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    async function fetchPageContent() {
      try {
        setLoading(true);
        setNotFound(false);

        const response = await contentfulClient.query<ContactUsPageResponse>(
          GetContactUsPage,
          {
            locale: localeConfig.currentLocale ?? undefined,
          },
        );

        const pageItems = response.contactUsPageCollection?.items ?? [];

        if (pageItems.length === 0) {
          setNotFound(true);
          setPageData(null);
          return;
        }

        setPageData(pageItems[0]);
      } catch (error) {
        console.error("Error fetching contact us page content:", error);
        setNotFound(true);
        setPageData(null);
      } finally {
        setLoading(false);
      }
    }

    fetchPageContent();
  }, [localeConfig, localeConfig.currentLocale]);

  return {
    pageData,
    loading,
    notFound,
    currentLocale: localeConfig.currentLocale,
  };
}
